/**
 * Analytics command - displays API proxy pipeline metrics
 *
 * Fetches the rolling analytics summary from the API proxy's
 * /v1/analytics endpoint and renders it as a formatted terminal dashboard.
 */

import chalk from 'chalk';
import { API_PROXY_URL } from '@spacecode/shared/remote';

export interface AnalyticsOptions {
  secret?: string;
  json?: boolean;
  url?: string;
}

interface AnalyticsSummary {
  totalRequests: number;
  semanticCacheHits: number;
  semanticCacheMisses: number;
  semanticCacheSkips: number;
  contextCacheHits: number;
  contextCacheMisses: number;
  contextCacheSkips: number;
  domainCounts: Record<string, number>;
  avgLatencyMs: number;
  avgSemanticCacheScore: number;
  qualityGatePassCount: number;
  qualityGateFailCount: number;
  pipelineSuccessCount: number;
  pipelineFallthroughCount: number;
  lastUpdated: number;
}

function resolveSecret(options: AnalyticsOptions): string {
  const secret = options.secret || process.env.SPACECODE_DEBUG_SECRET;
  if (!secret) {
    console.error(chalk.red('Error: No debug secret configured.'));
    console.log(chalk.dim('Set SPACECODE_DEBUG_SECRET env var or pass --secret <secret>'));
    process.exit(1);
  }
  return secret;
}

function pct(n: number, total: number): string {
  if (total === 0) return '0.0%';
  return `${((n / total) * 100).toFixed(1)}%`;
}

function bar(n: number, total: number, width = 20): string {
  if (total === 0) return chalk.dim('░'.repeat(width));
  const filled = Math.round((n / total) * width);
  return chalk.green('█'.repeat(filled)) + chalk.dim('░'.repeat(width - filled));
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTimestamp(ts: number): string {
  if (!ts) return 'never';
  const d = new Date(ts);
  const age = Date.now() - ts;
  const mins = Math.floor(age / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return d.toISOString().replace('T', ' ').slice(0, 19);
}

function renderDashboard(summary: AnalyticsSummary): void {
  const { totalRequests: total } = summary;

  console.log(chalk.bold('\n  API Proxy Analytics Dashboard\n'));
  console.log(chalk.dim(`  Last updated: ${formatTimestamp(summary.lastUpdated)}`));
  console.log(chalk.dim(`  Total requests: ${total.toLocaleString()}\n`));

  // ── Pipeline ────────────────────────────────────────────────
  console.log(chalk.bold('  Pipeline'));
  const pipelineTotal = summary.pipelineSuccessCount + summary.pipelineFallthroughCount;
  console.log(`    Success:      ${bar(summary.pipelineSuccessCount, pipelineTotal)} ${summary.pipelineSuccessCount} (${pct(summary.pipelineSuccessCount, pipelineTotal)})`);
  console.log(`    Fallthrough:  ${bar(summary.pipelineFallthroughCount, pipelineTotal)} ${summary.pipelineFallthroughCount} (${pct(summary.pipelineFallthroughCount, pipelineTotal)})`);
  console.log('');

  // ── Semantic Cache ──────────────────────────────────────────
  console.log(chalk.bold('  Semantic Cache'));
  const semTotal = summary.semanticCacheHits + summary.semanticCacheMisses + summary.semanticCacheSkips;
  console.log(`    Hit:          ${bar(summary.semanticCacheHits, semTotal)} ${summary.semanticCacheHits} (${pct(summary.semanticCacheHits, semTotal)})`);
  console.log(`    Miss:         ${bar(summary.semanticCacheMisses, semTotal)} ${summary.semanticCacheMisses} (${pct(summary.semanticCacheMisses, semTotal)})`);
  console.log(`    Skip:         ${bar(summary.semanticCacheSkips, semTotal)} ${summary.semanticCacheSkips} (${pct(summary.semanticCacheSkips, semTotal)})`);
  if (summary.avgSemanticCacheScore > 0) {
    console.log(`    Avg score:    ${summary.avgSemanticCacheScore.toFixed(4)}`);
  }
  console.log('');

  // ── Context Cache ───────────────────────────────────────────
  console.log(chalk.bold('  Context Cache'));
  const ctxTotal = summary.contextCacheHits + summary.contextCacheMisses + summary.contextCacheSkips;
  console.log(`    Hit:          ${bar(summary.contextCacheHits, ctxTotal)} ${summary.contextCacheHits} (${pct(summary.contextCacheHits, ctxTotal)})`);
  console.log(`    Miss:         ${bar(summary.contextCacheMisses, ctxTotal)} ${summary.contextCacheMisses} (${pct(summary.contextCacheMisses, ctxTotal)})`);
  console.log(`    Skip:         ${bar(summary.contextCacheSkips, ctxTotal)} ${summary.contextCacheSkips} (${pct(summary.contextCacheSkips, ctxTotal)})`);
  console.log('');

  // ── Quality Gate ────────────────────────────────────────────
  console.log(chalk.bold('  Quality Gate'));
  const qgTotal = summary.qualityGatePassCount + summary.qualityGateFailCount;
  console.log(`    Pass:         ${bar(summary.qualityGatePassCount, qgTotal)} ${summary.qualityGatePassCount} (${pct(summary.qualityGatePassCount, qgTotal)})`);
  console.log(`    Fail:         ${bar(summary.qualityGateFailCount, qgTotal)} ${summary.qualityGateFailCount} (${pct(summary.qualityGateFailCount, qgTotal)})`);
  console.log('');

  // ── Latency ─────────────────────────────────────────────────
  console.log(chalk.bold('  Latency'));
  console.log(`    Average:      ${formatMs(summary.avgLatencyMs)}`);
  console.log('');

  // ── Domain Distribution ─────────────────────────────────────
  const domains = Object.entries(summary.domainCounts).sort((a, b) => b[1] - a[1]);
  if (domains.length > 0) {
    console.log(chalk.bold('  Domain Distribution'));
    const domainTotal = domains.reduce((sum, [, count]) => sum + count, 0);
    for (const [domain, count] of domains) {
      const label = domain.padEnd(14);
      console.log(`    ${label} ${bar(count, domainTotal)} ${count} (${pct(count, domainTotal)})`);
    }
    console.log('');
  }
}

export async function analytics(options: AnalyticsOptions): Promise<void> {
  const secret = resolveSecret(options);

  console.log(chalk.dim('Fetching analytics...'));

  const baseUrl = options.url || API_PROXY_URL;

  try {
    const resp = await fetch(`${baseUrl}/v1/analytics`, {
      headers: { 'X-Debug-Secret': secret },
    });

    if (resp.status === 401) {
      console.error(chalk.red('Error: Unauthorized. Check your debug secret.'));
      process.exit(1);
    }

    if (!resp.ok) {
      console.error(chalk.red(`Error: HTTP ${resp.status} from API proxy.`));
      process.exit(1);
    }

    const data = await resp.json() as AnalyticsSummary | { error: string };

    if ('error' in data) {
      console.log(chalk.yellow(`\n  ${data.error}\n`));
      console.log(chalk.dim('  Analytics data will appear after requests flow through the pipeline.'));
      return;
    }

    if (options.json) {
      console.log(JSON.stringify(data, null, 2));
    } else {
      renderDashboard(data);
    }
  } catch (error) {
    console.error(chalk.red(`Error: ${error instanceof Error ? error.message : String(error)}`));
    process.exit(1);
  }
}
